/**
 * Service Suivi « vue d'ensemble » — READ-ONLY
 *
 * Calcule, pour chaque apprenant, sa situation GLOBALE en agregeant TOUTES
 * ses formations (et non une formation isolee). Source unique de verite pour
 * le badge « Suivi » au niveau apprenant : page « Mes apprenants », onglet
 * « Apprenants » des groupes et petite table du tableau de bord doivent rester
 * coherents.
 *
 * Regles (memes seuils que partout — localStorage) :
 *  - Compte desactive            → gere a l'affichage (« — »)
 *  - Rien commence du tout       → « Pas demarre depuis X j » (plus ancienne inscription)
 *  - Commence, formations restantes, plus d'activite recente → « En retard depuis X j »
 *  - Sinon (activite recente ou tout termine) → « En formation » (pas d'alerte)
 */
import { supabase } from '../config/supabase';

const DEFAULT_DELAY_DAYS = 14;
const DEFAULT_INACTIVE_DAYS = 21;
const getDelayThreshold = () => parseInt(localStorage.getItem('dlm_delay_threshold') || String(DEFAULT_DELAY_DAYS));
const getInactiveThreshold = () => parseInt(localStorage.getItem('dlm_inactive_threshold') || String(DEFAULT_INACTIVE_DAYS));

export interface LearnerTrackingAlert {
  type: 'delayed' | 'inactive';
  daysSince: number;
}

export interface LearnerTracking {
  alert?: LearnerTrackingAlert;
  completed: number;
  total: number;
}

/**
 * Retourne une Map learnerId -> { alert?, completed, total } pour les apprenants donnes.
 * Un seul SELECT (READ-ONLY) sur enrollments + course_progress, agrege en JS.
 */
export async function getLearnerTrackingMap(learnerIds: string[]): Promise<Map<string, LearnerTracking>> {
  const result = new Map<string, LearnerTracking>();
  if (learnerIds.length === 0) return result;

  const nowMs = Date.now();
  const delayMs = getDelayThreshold() * 86400000;
  const inactiveMs = getInactiveThreshold() * 86400000;

  const { data } = await supabase
    .from('enrollments')
    .select('learner_id, enrolled_at, course_progress(progress_percent, completed_at, last_activity_at)')
    .in('learner_id', learnerIds);

  interface Acc {
    total: number; completed: number;
    hasStarted: boolean; hasUnfinished: boolean;
    lastActivityMs: number; oldestEnrollMs: number;
  }
  const accMap = new Map<string, Acc>();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  for (const e of ((data || []) as any[])) {
    const cp = Array.isArray(e.course_progress) ? e.course_progress[0] : e.course_progress;
    const progress: number = cp?.progress_percent ?? 0;
    const isCompleted = !!cp?.completed_at || progress >= 100;
    const started = progress > 0 || isCompleted;

    const acc = accMap.get(e.learner_id) || {
      total: 0, completed: 0, hasStarted: false, hasUnfinished: false,
      lastActivityMs: 0, oldestEnrollMs: Infinity,
    };
    acc.total += 1;
    if (isCompleted) acc.completed += 1; else acc.hasUnfinished = true;
    if (started) acc.hasStarted = true;
    if (cp?.last_activity_at) {
      const t = new Date(cp.last_activity_at).getTime();
      if (t > acc.lastActivityMs) acc.lastActivityMs = t;
    }
    if (e.enrolled_at) {
      const t = new Date(e.enrolled_at).getTime();
      if (t < acc.oldestEnrollMs) acc.oldestEnrollMs = t;
    }
    accMap.set(e.learner_id, acc);
  }

  accMap.forEach((acc, learnerId) => {
    let alert: LearnerTrackingAlert | undefined;
    if (!acc.hasStarted) {
      // Rien commence du tout → « Pas demarre depuis X j »
      if (acc.oldestEnrollMs !== Infinity && nowMs - acc.oldestEnrollMs > inactiveMs) {
        alert = { type: 'inactive', daysSince: Math.floor((nowMs - acc.oldestEnrollMs) / 86400000) };
      }
    } else if (acc.hasUnfinished && acc.lastActivityMs > 0 && nowMs - acc.lastActivityMs > delayMs) {
      // Commence, formations restantes, plus d'activite recente → « En retard depuis X j »
      alert = { type: 'delayed', daysSince: Math.floor((nowMs - acc.lastActivityMs) / 86400000) };
    }
    result.set(learnerId, { alert, completed: acc.completed, total: acc.total });
  });

  return result;
}
